import { dayjs, ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { ApiStatus, ContractOrderType, ContractServices, OrderDirection, TriggerType } from 'network'
import { useBaseInfoStore, useDoubleContractKlineStore, useDoubleContractOrderListStore, useExchangeInfoStore, useProjectTheme, useResponsive } from 'store'
import { ControlledAmount, CustomButton, EmptyData, FullScreenMinHeight, HocElTable, ListCell, Tag, UiUtils } from 'ui-components'
import '../styles/order-list-layout.scss'
import '../styles/position-item-layout.scss'

// 当前委托
export const LimitOrders = defineComponent({
  setup() {
    const { t } = useI18n()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const responsive = useResponsive()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const klineStore = useDoubleContractKlineStore()
    const orderListStore = useDoubleContractOrderListStore()

    const orders = computed(() => {
      return orderListStore.limitOrders.filter((item) => {
        if (orderListStore.currentHideOtherOrder) {
          return item.symbolId === klineStore.currentSymbolId
        }
        return true
      })
    })

    const handleCancelOrder = (data: ApiResponse.DoubleContractLimitOrder) => {
      UiUtils.showModal(t('是否确认撤销此订单？'), {
        onConfirm: async () => {
          const res = await ContractServices.doubleContractCancelOrder({ symbolId: data.symbolId, trustId: data.trustId.toString() })
          if (res.status === ApiStatus.Success) {
            orderListStore.refreshLimitOrders()
            UiUtils.showSuccess(t('撤销成功'))
          }
        },
      })
    }

    return () => {
      if (responsive.isMobile) {
        if (orders.value.length === 0) {
          return (
            <div>
              <EmptyData />
            </div>
          )
        }
        return orders.value.map((item) => {
          const orderType = orderListStore.getOrderType(item)

          const closeType = orderListStore.getCloseOrOpenTag(item)

          const { triggerType, triggerPrice } = item

          const dir = orderListStore.getTriggerCondition(item)

          return (
            <div class="position-item-layout position-item divider">
              <div class="symbol-coin-info flex justify-between w-full !mb-[8px]">
                <div class="flex flex-row items-center">
                  <img width={32} height={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(item.symbolId)} alt="coin icon" />
                  <div class="coin-info">
                    <div class="coin-name">
                      {exchangeStore.getContractSymbolName(item.symbolId)} {t('永续')}
                    </div>
                    <div class="flex info-tags gap-4">
                      {closeType && (
                        <Tag class="!text-[10px]" color={item.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>
                          {t(closeType)}
                        </Tag>
                      )}

                      <Tag class="!text-[10px]" color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {item.type === ContractOrderType.Limit ? t('限价') : t('市价')}
                      </Tag>

                      {orderType && (
                        <Tag class="!text-[10px]" color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                          {t(orderType)}
                        </Tag>
                      )}
                      <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                        {item.marginType === 1 ? t('逐仓') : t('全仓')}
                        {item.leverage}X
                      </Tag>
                    </div>
                  </div>
                </div>
                <CustomButton class="!bg-[var(--colors-bg-tag-base)] !border-none" onClick={() => handleCancelOrder(item)}>
                  {t('撤销')}
                </CustomButton>
              </div>

              {/* 数量 */}
              <ListCell title={t('数量')} description={<ControlledAmount>{orderListStore.getQty(item)}</ControlledAmount>} />

              {/* 委托价格 */}
              <ListCell
                title={t('委托价格')}
                description={<ControlledAmount>{<span>{item.type === ContractOrderType.Limit ? item.price.formatCommonMoney() : '--'}</span>}</ControlledAmount>}
              />

              {/* 触发价格 */}
              {dir && (
                <ListCell
                  title={t('触发价格')}
                  description={
                    <span>
                      {triggerType === TriggerType.Latest ? t('最新价格') : t('标记价格')}
                      {dir}
                      {triggerPrice.formatCommonMoney()}
                    </span>
                  }
                />
              )}

              {/* 状态 */}
              {orderListStore.getStatus(item) !== '' && <ListCell title={t('状态')} description={<span>{orderListStore.getStatus(item)}</span>} />}

              {/* 下单时间 */}
              <ListCell title={t('下单时间')} description={<span>{dayjs(item.createTime).format('YYYY-MM-DD HH:mm:ss')}</span>} />
            </div>
          )
        })
      }

      return (
        <div class="contract-trade-order-list-layout">
          <HocElTable data={orders.value}>
            {{
              default: () => (
                <>
                  <ElTableColumn prop="symbolId" label={t('合约')} minWidth={140}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        const orderType = orderListStore.getOrderType(row)
                        const closeType = orderListStore.getCloseOrOpenTag(row)
                        const typeStr = row.type === ContractOrderType.Limit ? t('限价') : t('市价')
                        return (
                          <div class={'flex align-center gap-row-8 align-content-start'}>
                            <img width={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(row.symbolId)} alt="" />
                            <div class={'symbol-info'}>
                              <span class="symbol-name">
                                {exchangeStore.getContractSymbolName(row.symbolId)} {t('永续')}
                              </span>
                              <div class="flex info-tags gap-4">
                                {closeType && <Tag color={row.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{t(closeType)}</Tag>}
                                <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                  {typeStr}
                                </Tag>
                                {orderType && (
                                  <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                                    {t(orderType)}
                                  </Tag>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="qty" label={t('数量')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        return <ControlledAmount>{orderListStore.getQty(row)}</ControlledAmount>
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="marginType" label={t('保证金模式')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractPosition }) => {
                        return (
                          <Tag color={theme.value.colors.text.base} bgColor={theme.value.colors.bg.tag.base}>
                            {row.marginType === 1 ? t('逐仓') : t('全仓')}
                            {row.leverage}X
                          </Tag>
                        )
                      },
                    }}
                  </ElTableColumn>

                  <ElTableColumn prop="direction" label={t('触发价格')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        if (row.type === ContractOrderType.Limit) {
                          return '--'
                        }
                        const { triggerType, triggerPrice } = row
                        const triggerTypeStr = triggerType === TriggerType.Latest ? t('最新价格') : t('标记价格')
                        const dir = orderListStore.getTriggerCondition(row)
                        if (!dir) return '--'
                        return (
                          <span>
                            {triggerTypeStr}
                            {dir}
                            {triggerPrice.formatCommonMoney()}
                          </span>
                        )
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="price" label={t('委托价格')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        if (row.type === ContractOrderType.Limit) {
                          return row.price.formatCommonMoney()
                        }
                        return '--'
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="status" label={t('状态')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        return <span>{orderListStore.getStatus(row) || '--'}</span>
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="createTime" label={t('下单时间')}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        return <span>{dayjs(row.createTime).format('YYYY-MM-DD HH:mm:ss')}</span>
                      },
                    }}
                  </ElTableColumn>
                  <ElTableColumn prop="updateTime" label={t('操作')} width={160}>
                    {{
                      default: ({ row }: { row: ApiResponse.DoubleContractLimitOrder }) => {
                        return (
                          <div class="action-btns">
                            <span onClick={() => handleCancelOrder(row)}>{t('撤销')}</span>
                          </div>
                        )
                      },
                    }}
                  </ElTableColumn>
                </>
              ),
              empty: () => {
                return (
                  <FullScreenMinHeight>
                    <EmptyData />
                  </FullScreenMinHeight>
                )
              },
            }}
          </HocElTable>
        </div>
      )
    }
  },
})
